// Keyboard shortcuts cheatsheet, opened by `?`.
//
// Single source of truth for what shortcuts exist. Each entry here also
// describes the binding that's actually wired up elsewhere — adding a new
// shortcut means updating both the binding (in dashboard.tsx /
// ticket-detail.tsx / App.tsx) AND adding a row here so the user knows
// it exists.

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface Row {
  keys: string[]; // each rendered as <kbd>
  label: string;
}

interface Section {
  title: string;
  rows: Row[];
}

const SECTIONS: Section[] = [
  {
    title: 'Anywhere',
    rows: [
      { keys: ['⌘', 'K'], label: 'Open command palette' },
      { keys: ['?'], label: 'Show this cheatsheet' },
    ],
  },
  {
    title: 'Tickets dashboard',
    rows: [
      { keys: ['/'], label: 'Focus search' },
      { keys: ['j'], label: 'Next ticket' },
      { keys: ['k'], label: 'Previous ticket' },
      { keys: ['↵'], label: 'Open selected ticket' },
      { keys: ['esc'], label: 'Clear the search text, then leave the box' },
    ],
  },
  {
    title: 'Inside a ticket',
    rows: [
      { keys: ['r'], label: 'Focus reply composer' },
      { keys: ['n'], label: 'Focus internal note' },
      { keys: ['a'], label: 'Assign to me' },
      { keys: ['e'], label: 'Mark resolved (and open the next ticket, if you came from a list)' },
      { keys: ['['], label: 'Previous ticket in the list you came from (also from an empty reply box)' },
      { keys: [']'], label: 'Next ticket in the list you came from (also from an empty reply box)' },
      { keys: ['⌘', 'K'], label: 'Then type an action: resolve, snooze, assign liz, p1, reopen…' },
      { keys: ['⌘', 'F'], label: 'Find in the conversation (F3 / ⇧F3 next, previous)' },
    ],
  },
  {
    title: 'In the reply composer',
    rows: [
      { keys: ['⌘', '↵'], label: 'Send reply' },
      { keys: ['⌘', '⇧', 'D'], label: 'AI Draft (what you typed is used as notes)' },
      { keys: ['⌘', '⇧', 'P'], label: 'Polish the current reply' },
      { keys: ['↵'], label: 'In the Guide AI box: Polish, or Draft when the reply is empty' },
      { keys: ['/'], label: 'Open templates menu' },
    ],
  },
];

export function ShortcutsCheatsheet({ open, onOpenChange }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h3 className="mb-1.5 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.title}
              </h3>
              <ul className="flex flex-col">
                {section.rows.map((row) => (
                  <li
                    key={row.label}
                    className="flex items-center justify-between gap-3 border-b border-border/40 py-1.5 last:border-0"
                  >
                    <span className="text-sm text-foreground">{row.label}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {row.keys.map((k, i) => (
                        <kbd
                          key={i}
                          className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border/70 bg-muted px-1.5 font-mono text-3xs font-medium text-foreground/80"
                        >
                          {k}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
        <p className="mt-2 text-2xs text-muted-foreground">
          Shortcuts are skipped while you're typing in an input or composer (so
          letters land in the field, not in the app).
        </p>
      </DialogContent>
    </Dialog>
  );
}
